import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { reducers } from './core/store/reducers';
import { EffectsModule } from '@ngrx/effects';
import { CoreContextEffects } from './core/store/core-context/core-context-effects';
import { MemberEffects } from './core/store/member/member-effects';
import { OffersEffects } from '@app/core/store/offers/offers-effects';
import { GamesEffects } from '@app/core/store/games/games-effects';
import { SpaycialIntegrationEffects } from '@app/core/store/spaycial-integration/spaycial-integration-effects';
import * as Sentry from '@sentry/angular';
import { DateFnsLocaleService } from '@app/core/settings/language/date-fns-locale.service';
import { ReCaptchaService } from '@app/core/recaptcha/re-captcha.service';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LegalDocumentsEffects } from '@app/core/store/legal-documents/effects';
import { ProgramsEffects } from '@app/core/store/programs/effects';
import { APP_BASE_HREF } from '@angular/common';
import { AppBase } from '@app/shared/models/app-base';
import { LegalDocumentLinksService } from './core/legal-documents/legal-document-links.service';
import { INTERCEPTORS_PROVIDERS } from './core/interceptors/interceptors.provieders';
import { MobileAppIntegrationService } from './mobile-app-integration/mobile-app-integration.service';
import { SettingsService } from '@app/core/settings/settings.service';
import { SpinnerComponent } from '@app/shared/branded-components/spinner/spinner.component';

export const STORE_EFFECTS = [
  CoreContextEffects,
  MemberEffects,
  OffersEffects,
  GamesEffects,
  SpaycialIntegrationEffects,
  LegalDocumentsEffects,
  ProgramsEffects,
];

export function storeDebug(reducer: ActionReducer<any>): ActionReducer<any> {
  return function (state, action) {
    if (environment.debugMode) {
      console.log('[STORE] pre state', state);
      console.log('[STORE] action', action);
    }

    return reducer(state, action);
  };
}

export const metaReducers: MetaReducer[] = [
  storeDebug,
];

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    ServiceWorkerModule.register(`${window.location.origin}/ngsw-worker.js`, {
      enabled: environment.registerServiceWorker,
      registrationStrategy: 'registerWhenStable:1000',
    }),
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot(STORE_EFFECTS),
    BrowserAnimationsModule,
    CoreModule,
    AppRoutingModule,
    LoadingBarModule,
    SpinnerComponent
  ],
  providers: [
    {
      provide: APP_BASE_HREF,
      useValue: `${AppBase.href}/`,
    },
    ...INTERCEPTORS_PROVIDERS,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ logErrors: true }),
    },
    provideAppInitializer((): void => {
      const service = inject(DateFnsLocaleService);
      service.init();
    }),
    provideAppInitializer((): void => {
      const service = inject(ReCaptchaService);
      service.init();
    }),
    provideAppInitializer((): void => {
      const service = inject(MobileAppIntegrationService);
      service.init();
    }),
    provideAppInitializer((): void => {
      const service = inject(SettingsService);
      service.init();
    }),
    LegalDocumentLinksService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
